import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import React, { useMemo } from 'react';
import styled from 'styled-components';
import GridProvider, { useGridState } from '../GridProvider';
import { toMediaQuery } from '../useBreakpointSelector';
import useLayoutEffect from '../useLayoutEffect';

function getBreakpointStyles(breakpoints) {
  return breakpoints.map((breakpoint, i, array) => {
    var nextBreakpoint = array[i + 1];
    var mediaQuery = toMediaQuery(breakpoint, nextBreakpoint);
    var key = "@media ".concat(mediaQuery);
    return {
      [key]: {
        gridRowGap: breakpoint.rowGap || breakpoint.gap,
        gridColumnGap: breakpoint.columnGap || breakpoint.gap,
        gridTemplateColumns: "repeat(".concat(breakpoint.columns, ", 1fr)")
      }
    };
  });
}

var Wrapper = styled.div.withConfig({
  displayName: "Grid__Wrapper",
  componentId: "sc-1vv1hup-0"
})(["\n  display: grid;\n\n  ", ";\n"], _ref => {
  var $breakpointStyles = _ref.$breakpointStyles;
  return $breakpointStyles;
});
var GridElement = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  var onStateChange = _ref2.onStateChange,
      rest = _objectWithoutProperties(_ref2, ["onStateChange"]);

  var state = useGridState();
  var breakpointStyles = useMemo(() => {
    if (!state) {
      return null;
    }

    return getBreakpointStyles(state.breakpoints);
  }, [state === null || state === void 0 ? void 0 : state.breakpoints]);
  useLayoutEffect(() => {
    if (onStateChange) {
      onStateChange(state);
    }
  }, [state, onStateChange]);
  return /*#__PURE__*/React.createElement(Wrapper, _extends({
    $breakpointStyles: breakpointStyles,
    ref: ref
  }, rest));
});
GridElement.displayName = 'Grid.GridElement';
var Grid = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  var breakpoints = _ref3.breakpoints,
      defaultBreakpoint = _ref3.defaultBreakpoint,
      onStateChange = _ref3.onStateChange,
      children = _ref3.children,
      rest = _objectWithoutProperties(_ref3, ["breakpoints", "defaultBreakpoint", "onStateChange", "children"]);

  var isSubgrid = !breakpoints;
  var parentGridState = useGridState();

  if (isSubgrid && !parentGridState) {
    throw new Error('<Grid> was created without breakpoints, but there is no <GridProvider> ancestor. ' + 'Define breakpoints to create a grid, or add <GridProvider> to create a subgrid.');
  }

  var grid = /*#__PURE__*/React.createElement(GridElement, _extends({
    onStateChange: onStateChange,
    ref: ref
  }, rest), children);

  if (isSubgrid) {
    // No need for a provider, there already is a provider ancestor.
    return grid;
  } // Otherwise, create our own provider.


  return /*#__PURE__*/React.createElement(GridProvider, {
    breakpoints: breakpoints,
    defaultBreakpoint: defaultBreakpoint
  }, grid);
});
Grid.displayName = 'Grid';
export default Grid;